// UAE Open Finance Standards - Data Clusters and Consent Management
// Based on UAE Open Finance Standards v2.1-rc2

// Consent States (Appendix A)
export type ConsentState =
  | 'Awaiting' // Consent awaiting authorization
  | 'Authorized' // Consent has been authorized by User
  | 'Rejected' // Consent was rejected by User
  | 'Canceled' // Consent was canceled by User or TPP
  | 'Consumed' // Single-use Consent has been used
  | 'Suspended' // Consent suspended by LFI (fraud concerns)
  | 'Paused' // Consent temporarily paused by User (TPP CMI only)
  | 'Expired' // Consent has expired

// Consent Types
export type ConsentType = 'DataSharing' | 'ServiceInitiation'

// VRP Payment Frequency
export type VRPFrequency = 'one-time' | 'monthly' | 'quarterly' | 'annual'

// VRP Payment Status
export type VRPPaymentStatus = 'pending' | 'completed' | 'failed' | 'scheduled'

// VRP Payment Purpose
export type VRPPaymentPurpose =
  | 'utility-bill'
  | 'subscription'
  | 'loan-repayment'
  | 'insurance'
  | 'rent'
  | 'other'

// VRP Payment Details
export interface VRPPayment {
  id: string
  date: string
  amount: number
  currency: string
  status: VRPPaymentStatus
  reference: string
  description: string
  recipient?: string
  failureReason?: string
}

// VRP Consent Details
export interface VRPDetails {
  frequency: VRPFrequency
  maxAmountPerPayment: number
  totalPaymentsAllowed: number
  paymentsCompleted: number
  firstPaymentDate: string
  lastPaymentDate?: string
  nextPaymentDate?: string
  reference: string // Bill account number
  paymentPurpose: VRPPaymentPurpose
  merchantName: string
  merchantLogo?: string
  allowBalanceCheck: boolean
}

// Transaction for Service Initiation consents
export interface Transaction {
  id: string
  date: string
  amount?: number
  currency?: string
  status: 'Completed' | 'Pending' | 'Failed'
  description: string
  recipient?: string
}

// Enhanced Consent Details for CMI
export interface ConsentDetails {
  id: string
  consentId: string // Full consent ID (e.g., "1234567890123456")
  type: ConsentType
  state: ConsentState

  // TPP Information
  tppName: string
  tppTradingName: string
  tppLegalName: string
  tppLogo: string
  merchantName?: string // For OBO (On Behalf Of) scenarios

  // LFI Information
  bankName: string
  bankLogo: string

  // Account Information
  accountType: string // "Current Account", "Savings", etc.
  accountNumber: string // Full account number
  accountLastFour: string // Last 4 digits for display
  accountNickname?: string // User-defined nickname

  // Dates
  connectedDate: string // When consent was created
  authorizedDate?: string // When consent was authorized
  expiryDate: string // When consent expires
  lastAccessDate?: string // Last data share or service initiation
  canceledDate?: string // When consent was canceled

  // Duration
  durationMonths: number

  // Permissions
  dataClusters: DataCluster[]

  // Selected Accounts (for multi-account consents)
  selectedAccounts: Array<{
    id: string
    name: string
    accountNumber: string
    accountType: string
  }>

  // Transaction Log (Service Initiation only)
  transactionLog?: Transaction[]

  // VRP Details (Service Initiation with VRP)
  vrpDetails?: VRPDetails
  paymentHistory?: VRPPayment[]

  // Metadata
  createdBy: 'User' | 'TPP' | 'LFI'
  lastModified: string
  version: number
}

// Data Cluster with expanded permissions
export interface DataCluster {
  cluster: string
  permissions: DataPermission[]
  lastAccessed?: string
  icon?: string // Icon name for UI display
}

// UAE Standard Data Clusters and Permissions
export interface DataPermission {
  id: string
  language: string
  examples: string
}

// Helper: Get icon name for data cluster
export function getDataClusterIcon(clusterName: string): string {
  const iconMap: Record<string, string> = {
    'Your Account details': 'User',
    'Your Regular Payments': 'DollarSign',
    'Your Account Transactions': 'CreditCard',
    'Your Product Information': 'Info',
    'Contact and Party Details': 'Users',
  }
  return iconMap[clusterName] || 'FileText'
}

// Helper: Get VRP frequency display text
export function getVRPFrequencyText(frequency: VRPFrequency): string {
  const frequencyMap: Record<VRPFrequency, string> = {
    'one-time': 'One-time payment',
    monthly: 'Monthly payments',
    quarterly: 'Quarterly payments',
    annual: 'Annual payments',
  }
  return frequencyMap[frequency]
}

// Helper: Get VRP payment purpose display text
export function getVRPPurposeText(purpose: VRPPaymentPurpose): string {
  const purposeMap: Record<VRPPaymentPurpose, string> = {
    'utility-bill': 'Utility Bill',
    subscription: 'Subscription',
    'loan-repayment': 'Loan Repayment',
    insurance: 'Insurance',
    rent: 'Rent',
    other: 'Other',
  }
  return purposeMap[purpose]
}

// Helper: Calculate next payment date
export function calculateNextPaymentDate(
  lastPaymentDate: string,
  frequency: VRPFrequency,
): string {
  const date = new Date(lastPaymentDate)

  switch (frequency) {
    case 'monthly':
      date.setMonth(date.getMonth() + 1)
      break
    case 'quarterly':
      date.setMonth(date.getMonth() + 3)
      break
    case 'annual':
      date.setFullYear(date.getFullYear() + 1)
      break
    case 'one-time':
      return lastPaymentDate
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

export const UAE_DATA_CLUSTERS: DataCluster[] = [
  {
    cluster: 'Your Account details',
    icon: 'User',
    permissions: [
      {
        id: 'account_details_1',
        language: 'Account name, type, and currency',
        examples: 'Current Account, AED',
      },
      {
        id: 'account_details_2',
        language: 'Account number and sort code',
        examples: '1234567890, 123456',
      },
      {
        id: 'account_details_3',
        language: 'Account balance',
        examples: 'AED 5,000.00',
      },
    ],
  },
  {
    cluster: 'Your Regular Payments',
    icon: 'DollarSign',
    permissions: [
      {
        id: 'regular_payments_1',
        language: 'Standing orders and direct debits',
        examples: 'Monthly rent, utility bills',
      },
      {
        id: 'regular_payments_2',
        language: 'Scheduled future payments',
        examples: 'Upcoming transfers',
      },
    ],
  },
  {
    cluster: 'Your Account Transactions',
    icon: 'CreditCard',
    permissions: [
      {
        id: 'transactions_1',
        language: 'Transaction history',
        examples: 'Past 90 days of transactions',
      },
      {
        id: 'transactions_2',
        language: 'Transaction details (date, amount, merchant)',
        examples: 'Carrefour AED 250.00 on 15/12/2024',
      },
    ],
  },
  {
    cluster: 'Your Product Information',
    icon: 'Info',
    permissions: [
      {
        id: 'product_info_1',
        language: 'Product details and features',
        examples: 'Account type, interest rate, fees',
      },
      {
        id: 'product_info_2',
        language: 'Credit limits and overdraft information',
        examples: 'Credit card limit AED 10,000',
      },
    ],
  },
  {
    cluster: 'Contact and Party Details',
    icon: 'Users',
    permissions: [
      {
        id: 'contact_1',
        language: 'Your name and contact information',
        examples: 'Full name, email, phone number',
      },
      {
        id: 'contact_2',
        language: 'Your address',
        examples: 'Residential address',
      },
    ],
  },
]

// Consent Configuration
export const CONSENT_CONFIG = {
  durationOptions: [1, 3, 6, 12, 24] as const,
  defaultDuration: 6,
  maxDuration: 24,
  minDuration: 1,
  historyRetentionMonths: 12, // 1 year minimum retention
  expiryWarningDays: 30, // Show warning when < 30 days to expiry
}

// VRP Configuration
export const VRP_CONFIG = {
  maxAmountLimits: [500, 1000, 2000, 5000, 10000] as const,
  defaultMaxAmount: 1000,
  maxPaymentsLimits: [1, 12, 24, 36, 60] as const,
  defaultMaxPayments: 12,
}

// Helper: Calculate expiry date
export function calculateExpiryDate(durationMonths: number): string {
  const expiry = new Date()
  expiry.setMonth(expiry.getMonth() + durationMonths)
  return expiry.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

// Helper: Check if consent is expiring soon
export function isExpiringSoon(expiryDate: string): boolean {
  const expiry = new Date(expiryDate)
  const now = new Date()
  const daysUntilExpiry = Math.floor(
    (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  )
  return (
    daysUntilExpiry <= CONSENT_CONFIG.expiryWarningDays && daysUntilExpiry > 0
  )
}

// Helper: Check if consent is expired
export function isExpired(expiryDate: string): boolean {
  const expiry = new Date(expiryDate)
  const now = new Date()
  return expiry < now
}

// Helper: Format consent ID for display (first 4 + last 4 digits)
export function formatConsentId(consentId: string): string {
  if (consentId.length <= 8) return consentId
  const first4 = consentId.substring(0, 4)
  const last4 = consentId.substring(consentId.length - 4)
  return `${first4}...${last4}`
}

// Helper: Get consent state color
export function getConsentStateColor(state: ConsentState): {
  bg: string
  text: string
  border: string
} {
  switch (state) {
    case 'Authorized':
      return { bg: '#E8F5E9', text: '#2E7D32', border: '#4CAF50' }
    case 'Awaiting':
      return { bg: '#FFF3E0', text: '#E65100', border: '#FF9800' }
    case 'Paused':
      return { bg: '#E3F2FD', text: '#1565C0', border: '#2196F3' }
    case 'Suspended':
      return { bg: '#FFF3E0', text: '#E65100', border: '#FF9800' }
    case 'Canceled':
      return { bg: '#F5F5F5', text: '#616161', border: '#9E9E9E' }
    case 'Rejected':
      return { bg: '#FFEBEE', text: '#C62828', border: '#F44336' }
    case 'Consumed':
      return { bg: '#F5F5F5', text: '#616161', border: '#9E9E9E' }
    case 'Expired':
      return { bg: '#F5F5F5', text: '#616161', border: '#9E9E9E' }
    default:
      return { bg: '#F5F5F5', text: '#616161', border: '#9E9E9E' }
  }
}

// Helper: Get VRP payment status color
export function getVRPPaymentStatusColor(status: VRPPaymentStatus): {
  bg: string
  text: string
} {
  switch (status) {
    case 'completed':
      return { bg: '#E8F5E9', text: '#2E7D32' }
    case 'pending':
      return { bg: '#FFF3E0', text: '#E65100' }
    case 'scheduled':
      return { bg: '#E3F2FD', text: '#1565C0' }
    case 'failed':
      return { bg: '#FFEBEE', text: '#C62828' }
    default:
      return { bg: '#F5F5F5', text: '#616161' }
  }
}

// Helper: Get display name for OBO scenarios
export function getConsentDisplayName(consent: ConsentDetails): string {
  if (consent.merchantName) {
    return `${consent.merchantName} via ${consent.tppTradingName}`
  }
  return consent.tppTradingName
}

// Helper: Format relative time
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  return date.toLocaleDateString('en-GB')
}
